// Libs
import React, { useCallback, useMemo } from 'react';
import {
  Card as MuiCard,
  CardActionArea,
  CardMedia,
  CardContent,
  Box,
  Typography,
  Stack,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

// Components
import FlagButton from '../../Button/FlagButton';
import Rating from '../../Rating';
import CountDescription, { StockItem } from './CountDescription';
import NewLabel from './NewLabel';
import ReportableIcon from './ReportableIcon';
import Image from '../../Image';
import Details from '../Details';
import MaterialsProductQtyButtons from './MaterialsProductQtyButtons';
import FdmButton from './FdmButton';
import CloseButton from '../../Button/CloseButton';
import LaterProductQtyButtons from '../../Cart/LaterProductQtyButtons/LaterProductQtyButtons';
import KitsProductQtyButtons from '../../Kits/KitsProductQtyButtons/KitsProductQtyButtons';
import KitProductQty from '../../Kits/KitProductQty/KitProductQty';

// Actions
import { OpenModal } from '../../../actions/modal';

// Hooks
import useCart from '../../../pages/Cart/hooks/useCart';
import useSaveForLater from '../../../pages/Cart/hooks/useSaveForLater';
import useKitsProducts from '../../../pages/Kits/hooks/useKitsProducts';
import useKitActions from '../../../pages/Kits/hooks/useKitActions';
import useFavorites from '../../../hooks/useFavorites';
import useUnavailableForOrderProduct from '../../../hooks/useUnavailableForOrderProduct';

// Utils
import isDigitalAsset from '../../../utils/isDigitalAsset';
import canRateProduct from '../../../utils/canRateProduct';
import useUI from '../../../hooks/useUI';

import { useStyles } from './GridCardStyles';

const IMAGE_HEIGHT_DESKTOP = 250;
const IMAGE_HEIGHT_TABLET = 224;
const IMAGE_HEIGHT_MOBILE = 138;

const GridCardRating = ({ isMobileMode, isRateable, product, forMobile }) => {
  const show = forMobile
    ? isMobileMode && isRateable
    : !isMobileMode && isRateable;

  if (!show) {
    return null;
  }
  return <Rating product={product} />;
};

const GridCardFlagButton = ({
  kit,
  isTabletMode,
  isFavorite,
  product,
  classes,
  setFavoriteValue,
}) => {
  if (kit || isTabletMode) {
    return null;
  }
  return (
    <FlagButton
      className={classes.flagButton}
      active={isFavorite}
      onClick={() => setFavoriteValue(product.product_id, !isFavorite)}
      id="flag-button"
    />
  );
};

const GridCardTabletStock = ({
  availableQuantity,
  unavailableLabel,
  isDigitalAssetProduct,
  isReportable,
  classes,
}) => (
  <Stack direction="row" justifyContent="space-between">
    <StockItem
      availableQuantity={availableQuantity}
      unavailableLabel={unavailableLabel}
      isDigitalAsset={isDigitalAssetProduct}
      className={classes.stockItem}
    />
    {isReportable && <ReportableIcon />}
  </Stack>
);

const GridCardDesktopDetails = ({
  perUnitQty,
  maxAllowedQty,
  availableQuantity,
  isDigitalAssetProduct,
  unavailableLabel,
  product,
  saveForLater,
  kit,
  isReportable,
  classes,
}) => (
  <Box display="flex" justifyContent="space-between" mt={1}>
    <CountDescription
      perUnitQty={perUnitQty}
      maxAllowedQty={maxAllowedQty}
      availableQuantity={availableQuantity}
      isDigitalAsset={isDigitalAssetProduct}
      view={'grid'}
      unavailableLabel={unavailableLabel}
    />
    <Stack>
      {!saveForLater && !kit && (
        <FdmButton className={classes.fdmButton} product={product} />
      )}
      {isReportable && (
        <Box marginTop="auto" alignSelf="end">
          <ReportableIcon />
        </Box>
      )}
    </Stack>
  </Box>
);

const GridCardDetailsSection = ({ isTabletMode, ...props }) => {
  if (isTabletMode) {
    return <GridCardTabletStock {...props} />;
  }
  return <GridCardDesktopDetails {...props} />;
};

const GridCardDefaultContent = ({
  kit,
  isTabletMode,
  isMobileMode,
  isFavorite,
  product,
  classes,
  description,
  sizeVers,
  isRateable,
  availableQuantity,
  unavailableLabel,
  isDigitalAssetProduct,
  isReportable,
  perUnitQty,
  maxAllowedQty,
  saveForLater,
  onOpenModal,
  setFavoriteValue,
  qtyButtonsContent,
}) => (
  <>
    <div>
      <GridCardRating
        forMobile
        isMobileMode={isMobileMode}
        isRateable={isRateable}
        product={product}
      />
      <div
        onClick={onOpenModal}
        className={classes.description}
        data-testid="product-description">
        {description}
      </div>
      <Typography variant="textXs" className={classes.sizeVers}>
        {sizeVers}
      </Typography>
      <GridCardRating
        isMobileMode={isMobileMode}
        isRateable={isRateable}
        product={product}
      />
      <GridCardDetailsSection
        isTabletMode={isTabletMode}
        availableQuantity={availableQuantity}
        unavailableLabel={unavailableLabel}
        isDigitalAssetProduct={isDigitalAssetProduct}
        isReportable={isReportable}
        perUnitQty={perUnitQty}
        maxAllowedQty={maxAllowedQty}
        product={product}
        saveForLater={saveForLater}
        kit={kit}
        classes={classes}
      />
    </div>
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      width={'100%'}
      gap={2}>
      {qtyButtonsContent}
      <GridCardFlagButton
        kit={kit}
        isTabletMode={isTabletMode}
        isFavorite={isFavorite}
        product={product}
        classes={classes}
        setFavoriteValue={setFavoriteValue}
      />
    </Box>
  </>
);

const GridCard = ({
  product,
  cardContent,
  cardModalComponent,
  imageContainer,
  modalProps = {},
  saveForLater,
  kit,
  nums,
  className,
  CloseComponent,
  readOnly = false,
  KitQtyComponent = KitsProductQtyButtons,
  onDeleteKitProduct,
  kitItemOrderable,
}) => {
  const dispatch = useDispatch();
  const { selectingKitProducts } = useSelector((state) => state.kits);

  const { isTabletMode, isMobileMode } = useUI();

  const {
    image_link: image,
    product_description: description,
    product_available_qty: availableQuantity,
    prepack_quantity: perUnitQty,
    order_limit: maxAllowedQty,
    is_new: isNew,
    is_favourite: isFavorite,
    product_id: id,
    reportable: isReportable,
    size_vers: sizeVers,
  } = product || {};
  const isRateable = canRateProduct(product);

  const {
    addInCart,
    addingProgress,
    count,
    addOne,
    removeOne,
    setCount,
    isProductInCart,
  } = useCart(id);
  const computedAvailability = useUnavailableForOrderProduct(product, true);
  const isDigitalAssetProduct = isDigitalAsset(product);
  const { isUnavailableForOrder, unavailableWatermark, unavailableLabel } =
    kitItemOrderable
      ? {
          isUnavailableForOrder: false,
          unavailableLabel: '',
          unavailableWatermark: <></>,
        }
      : computedAvailability;

  const { deleteFromLater } = useSaveForLater();

  const { classes, cx } = useStyles({ isUnavailableForOrder });

  const { removeProductFromKit } = useKitsProducts();

  const { onDeleteClick } = useKitActions(kit);

  const { setFavoriteValue } = useFavorites();

  const onOpenModal = useCallback(() => {
    let modalId;
    if (kit || selectingKitProducts) {
      modalId = 'kits-product-details';
    } else if (saveForLater) {
      modalId = 'later-product-details';
    } else {
      modalId = 'materials-product-details';
    }

    return dispatch(
      OpenModal({
        id: modalId,
        Component: cardModalComponent || Details,
        props: {
          productId: id,
          kitItemOrderable,
          readOnly,
          isFullScreen: isTabletMode,
          ...modalProps,
          nums,
          paddingContent: cardModalComponent
            ? {
                xl: '50px 40px 50px',
                lg: '60px 28px 30px',
                md: '40px 12px 16px',
              }
            : {
                xl: '50px 40px 40px',
                lg: '60px 28px 38px',
                md: '40px 12px 16px',
              },
        },
        styles: {
          width: 'min(90vw, 1143px)',
        },
      }),
    );
  }, [
    dispatch,
    kit,
    selectingKitProducts,
    saveForLater,
    cardModalComponent,
    kitItemOrderable,
    readOnly,
    id,
    isTabletMode,
    modalProps,
    nums,
  ]);

  const CloseIcon = useMemo(() => {
    if (saveForLater) {
      return (
        <CloseButton
          onClose={(e) => {
            e.stopPropagation();
            deleteFromLater(product?.product_id);
          }}
          className={classes.removeButton}
        />
      );
    }
    if (kit && !readOnly) {
      return (
        <CloseButton
          onClose={(e) => {
            e.stopPropagation();
            if (onDeleteKitProduct) {
              onDeleteKitProduct(product?.product_id);
            } else if (kit.products.length === 1) {
              onDeleteClick(kit.id);
            } else {
              removeProductFromKit(kit.id, product?.product_id);
            }
          }}
          className={classes.removeButton}
        />
      );
    }
    return <></>;
  }, [
    saveForLater,
    kit,
    readOnly,
    onDeleteKitProduct,
    classes.removeButton,
    deleteFromLater,
    product?.product_id,
    removeProductFromKit,
    onDeleteClick,
  ]);

  const qtyButtonsContent = useMemo(() => {
    if (saveForLater) {
      return (
        <LaterProductQtyButtons
          product={product}
          isMobileMode={isMobileMode}
          isProductInCart={false}
          nums={nums}
          className={classes.laterQtyButtons}
        />
      );
    }
    // Another buttons should be displayed only when available qty > 0
    if (availableQuantity > 0 || isDigitalAssetProduct) {
      if (kit || selectingKitProducts) {
        return (
          !isUnavailableForOrder &&
          (readOnly ? (
            <KitProductQty nums={nums} className={classes.kitsQtyButtons} />
          ) : (
            <KitQtyComponent
              product={product}
              isMobileMode={isMobileMode}
              detailsMode={false}
              listView={false}
              nums={nums}
              className={classes.kitsQtyButtons}
            />
          ))
        );
      }
      return (
        !isUnavailableForOrder && (
          <MaterialsProductQtyButtons
            loading={addingProgress}
            listView={false}
            product={product}
            isMobileMode={isMobileMode}
            addInCart={addInCart}
            count={count}
            addOne={addOne}
            removeOne={removeOne}
            setCount={setCount}
            isProductInCart={isProductInCart}
            className={classes.materialsQtyButtons}
          />
        )
      );
    }
    return <></>;
  }, [
    saveForLater,
    product,
    isMobileMode,
    nums,
    classes.laterQtyButtons,
    availableQuantity,
    isDigitalAssetProduct,
    kit,
    selectingKitProducts,
    isUnavailableForOrder,
    readOnly,
    classes.kitsQtyButtons,
    addingProgress,
    addInCart,
    count,
    addOne,
    removeOne,
    setCount,
    isProductInCart,
    classes.materialsQtyButtons,
  ]);

  return (
    <MuiCard className={cx(className, classes.root)}>
      <div onClick={(e) => e.stopPropagation()} className={classes.labels}>
        {isNew && <NewLabel positionStatic />}
      </div>
      <CardActionArea
        disabled={!image}
        className={classes.actionArea}
        onClick={onOpenModal}>
        {unavailableWatermark}
        {CloseIcon}
        {CloseComponent && <CloseComponent />}
        <Image
          alt="productImage"
          imageContainer={
            imageContainer || {
              desktop: ['100%', IMAGE_HEIGHT_DESKTOP],
              tablet: ['100%', IMAGE_HEIGHT_TABLET],
              mobile: ['100%', IMAGE_HEIGHT_MOBILE],
            }
          }
          src={image}>
          <CardMedia
            id="image-button"
            className={classes.img}
            component="img"
            image={image}
            alt="productImage"
          />
        </Image>
      </CardActionArea>
      <CardContent className={classes.content}>
        {!cardContent ? (
          <GridCardDefaultContent
            kit={kit}
            isTabletMode={isTabletMode}
            isMobileMode={isMobileMode}
            isFavorite={isFavorite}
            product={product}
            classes={classes}
            description={description}
            sizeVers={sizeVers}
            isRateable={isRateable}
            availableQuantity={availableQuantity}
            unavailableLabel={unavailableLabel}
            isDigitalAssetProduct={isDigitalAssetProduct}
            isReportable={isReportable}
            perUnitQty={perUnitQty}
            maxAllowedQty={maxAllowedQty}
            saveForLater={saveForLater}
            onOpenModal={onOpenModal}
            setFavoriteValue={setFavoriteValue}
            qtyButtonsContent={qtyButtonsContent}
          />
        ) : (
          <>{cardContent}</>
        )}
      </CardContent>
    </MuiCard>
  );
};

export default GridCard;
